/**
 * Reward Composable
 * 管理獎勵相關功能
 * 原始來源: cuora/src/machine/reward.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/rewards/reward.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { nextTick, onBeforeUnmount, getCurrentInstance } from 'vue'

import { activityApi, gameApi } from '@/api'
import { useGameRoomDefault } from '@/composables/game/useGameRoomDefault'
import { useMainStore } from '@/stores/main'
import { useRewardStore } from '@/stores/rewards/reward'
import { toClaimReceiveNo } from '@/utils/identityGuard'
import { isRequestErrorAlreadySurfaced } from '@/utils/requestError'

// 領獎後要開的 Inbox 頁籤，依獎勵類別決定（TI-15786）。
// 後端把獎勵切成 CASH,FREE,PRIZE（REWARD 頁籤）與 POINTS（DIAMOND 頁籤）兩組查詢，這裡與之對齊。
const INBOX_TAB_TYPE_BY_REWARD_TYPE = {
  POINTS: 'DIAMOND',
  GAME: 'FREE_GAME',
  CASH: 'REWARD',
  FREE: 'REWARD',
  PRIZE: 'REWARD',
}

const FREE_REWARD_RETURN_DELAY_MS = 1000

const receiveNosOf = (rows) => (Array.isArray(rows) ? rows.map((row) => row?.receiveNo) : [])

export const useReward = () => {
  const nuxtApp = useNuxtApp()
  const { $request, $i18n } = nuxtApp
  const { appSecret } = useRuntimeConfig().public
  const { isLoggedIn } = useAuthToken()
  const mainStore = useMainStore()
  const { setLoading, setModal, setModalParams } = mainStore
  const { resetToDefaultRoom } = useGameRoomDefault()
  const identity = useIdentityScope()
  const { claimReward, previewReward } = useRewardClaim()

  const store = useRewardStore()
  const {
    otherRewardList,
    pointsRewardList,
    rewardList,
    freeGameList,
    activityNo,
    receiveNo,
    withdrawRule,
    rewardType,
    isPlaying,
    callback,
    orderColumn,
    lang,
    receiving,
    pending,
    getPending,
  } = storeToRefs(store)

  // Initialize lang from i18n
  if ($i18n?.locale?.value && !lang.value) {
    store.setLang($i18n.locale.value)
  }

  // SSR-safe: 使用 nuxtApp context 存儲 timer，避免記憶體洩漏
  if (!nuxtApp._rewardContext) {
    nuxtApp._rewardContext = {
      refreshTimer: null,
      // SSR cleanup 方法
      cleanup: () => {
        const ctx = nuxtApp._rewardContext
        if (ctx?.refreshTimer) {
          clearInterval(ctx.refreshTimer)
          ctx.refreshTimer = null
        }
      },
    }
  }
  const ctx = nuxtApp._rewardContext

  const fetchRewardList = async () => {
    if (!isLoggedIn.value) {
      return
    }

    // 必須在 await 之前取：receiveNo 歸屬於「取得它時所用的 token」（spec 2026-10-19 BR-1）
    const requestIdentity = identity.captureRequestIdentity()

    try {
      // 並行載入三種獎勵列表
      const [otherRewardRes, pointsRewardRes, rewardRes] = await Promise.all([
        $request.send(
          activityApi.getRewardList({
            rewardType: 'CASH,FREE,PRIZE',
            count: 30,
            appSecret,
            orderColumn: orderColumn.value,
            lang: lang.value,
          })
        ),
        $request.send(
          activityApi.getRewardList({
            rewardType: 'POINTS',
            count: 30,
            appSecret,
            lang: lang.value,
          })
        ),
        $request.send(
          activityApi.getRewardList({
            count: 30,
            appSecret,
            lang: lang.value,
          })
        ),
      ])

      identity.admit(requestIdentity, [...receiveNosOf(otherRewardRes.data), ...receiveNosOf(pointsRewardRes.data), ...receiveNosOf(rewardRes.data)])

      // 飛行中換了 token：這批是前一個身分的，不寫入畫面（REQ-7）
      if (identity.isRequestIdentityCurrent(requestIdentity)) {
        store.setOtherRewardList(otherRewardRes.data || [])
        store.setPointsRewardList(pointsRewardRes.data || [])
        store.setRewardList(rewardRes.data || [])
      }

      return {
        otherRewardList: otherRewardList.value,
        pointsRewardList: pointsRewardList.value,
        rewardList: rewardList.value,
      }
    } catch (error) {
      console.error('[useReward] fetchRewardList error:', error)
      throw error
    }
  }

  const fetchOtherRewardList = async (newOrderColumn = 'createTime') => {
    store.setOrderColumn(newOrderColumn)

    const requestIdentity = identity.captureRequestIdentity()

    try {
      const response = await $request.send(
        activityApi.getRewardList({
          rewardType: 'CASH,FREE,PRIZE',
          count: 30,
          appSecret,
          orderColumn: newOrderColumn,
          lang: lang.value,
        })
      )

      const data = response.data || response
      identity.admit(requestIdentity, receiveNosOf(data))
      if (identity.isRequestIdentityCurrent(requestIdentity)) {
        store.setOtherRewardList(data)
      }

      return otherRewardList.value
    } catch (error) {
      console.error('[useReward] fetchOtherRewardList error:', error)
      throw error
    }
  }

  const fetchFreeGameList = async () => {
    const requestIdentity = identity.captureRequestIdentity()
    const { data } = await $request.send(gameApi.getFreeGameList())

    identity.admit(requestIdentity, receiveNosOf(data))
    if (identity.isRequestIdentityCurrent(requestIdentity)) {
      store.setFreeGameList(data)
    }

    return freeGameList.value
  }

  const withdrawData = ref({})
  const fetchWithdrawRulePreview = async ({ activityNo, receiveNo }) => {
    setLoading({ inbox: true })
    store.setActivityNo(activityNo)
    store.setReceiveNo(receiveNo)

    try {
      const response = await previewReward({ receiveNo })

      // 確保 response.data 不存在時也有預設物件，避免下游拿到 undefined
      const data = response.data || response || {}
      withdrawData.value = data
      store.setWithdrawRule(data)
      store.setPending(true)
    } catch (error) {
      console.error('[useReward] fetchWithdrawRulePreview error:', error)
    } finally {
      setLoading({ inbox: false })
    }
  }

  const back = () => {
    store.resetPendingState()
  }

  // 領獎後開啟 Inbox 的唯一入口。一律帶上 tabType，Inbox 才不會退回未讀優先序 —— 規格要求
  // 領獎路徑只看獎勵類別、不看未讀狀態。未列舉的類別（含一次領到多種）都回 REWARD。
  const openInboxForRewardType = async (type) => {
    setModal(null)
    await nextTick()
    const tabType = INBOX_TAB_TYPE_BY_REWARD_TYPE[type] || 'REWARD'
    setModalParams({ tabType })
    setModal('ModalInbox')
  }

  // FREE_ 規則的獎勵會換掉提款規則，館別清單跟著變
  const resetToDefaultRoomAfterFreeReward = async (originalModal) => {
    if (!import.meta.client) return
    await new Promise((resolve) => setTimeout(resolve, FREE_REWARD_RETURN_DELAY_MS))
    await resetToDefaultRoom({ originalModal })
  }

  const receiveReward = async ({
    activityNo: newActivityNo,
    receiveNo: newReceiveNo,
    attended,
    callback: rewardCallback,
    rewardType: newRewardType,
    withdrawRule: rewardWithdrawRule = '',
  }) => {
    const originalModal = mainStore.modal
    let data
    store.setReceiving(true)
    store.setCallback(rewardCallback || null)
    store.setRewardType(newRewardType || null)

    try {
      const validateReceiveNo = toClaimReceiveNo(newReceiveNo)

      // 重置 pending 狀態
      store.setPending(false)
      setLoading({ inbox: true })

      const response = await claimReward({
        activityNo: newActivityNo,
        receiveNo: validateReceiveNo,
        attended,
      })

      data = response.data || response

      // 刷新獎勵列表
      await fetchRewardList()

      // 如果不是 PRIZE 類型，執行回調
      if (newRewardType !== 'PRIZE' && rewardCallback) {
        rewardCallback(data)
      }
    } catch (error) {
      store.setPending(false)
      console.error('[useReward] receiveReward error:', error)
      throw error
    } finally {
      store.setReceiving(false)
      setLoading({ inbox: false })
    }

    // 領取已成功，回首頁屬附帶動作，不阻塞也不讓其失敗改變領取結果
    if (rewardWithdrawRule.includes('FREE_')) {
      resetToDefaultRoomAfterFreeReward(originalModal).catch((error) => {
        if (!isRequestErrorAlreadySurfaced(error)) throw error
      })
    }
    return data
  }

  const receiveAllPoints = async (callbackOrOptions) => {
    store.setReceiving(true)

    // Support both old signature (callback) and new signature ({ callback })
    const rewardCallback = typeof callbackOrOptions === 'function' ? callbackOrOptions : callbackOrOptions?.callback

    store.setCallback(rewardCallback || null)

    try {
      // 不經領獎出口：一鍵領取不帶任何編號，後端以 token 決定領誰的（spec 2026-10-19 design §7.1 決策 9）
      const response = await $request.send(
        activityApi.collect({
          collectType: 'collectPoints',
          appSecret,
        })
      )

      const data = response.data || response

      // 刷新獎勵列表
      await fetchRewardList()

      // 執行回調
      if (rewardCallback) {
        rewardCallback(data)
      }

      return data
    } catch (error) {
      console.error('[useReward] receiveAllPoints error:', error)
      throw error
    } finally {
      store.setReceiving(false)
    }
  }

  const setIsPlaying = (newIsPlaying) => {
    store.setIsPlaying(newIsPlaying)
  }

  const setLang = (locale) => {
    store.setLang(locale || lang.value || 'en-US')
  }

  const refresh = async () => {
    await fetchRewardList()
  }

  const startAutoRefresh = () => {
    if (!import.meta.client) return

    stopAutoRefresh()

    ctx.refreshTimer = setInterval(() => {
      // 如果不在遊戲中，才自動刷新
      if (!isPlaying.value) {
        // 背景輪詢用：吞掉 rejection，避免 unhandledrejection 上報 Sentry
        // fetchRewardList 內已 console.error；真實錯誤已於 request 層上報，失敗等下一輪（20s）自動重試
        fetchRewardList().catch(() => {})
      }
    }, 20000)
  }

  const stopAutoRefresh = () => {
    if (!import.meta.client) return

    if (ctx.refreshTimer) {
      clearInterval(ctx.refreshTimer)
      ctx.refreshTimer = null
    }
  }

  // 元件卸載時清理 timer，避免記憶體洩漏
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopAutoRefresh()
    })
  }

  return {
    otherRewardList,
    pointsRewardList,
    rewardList,
    freeGameList,
    activityNo,
    receiveNo,
    withdrawRule,
    rewardType,
    isPlaying,
    callback,
    orderColumn,
    lang,
    receiving,
    pending,
    getPending,
    withdrawData,
    fetchRewardList,
    fetchOtherRewardList,
    fetchFreeGameList,
    fetchWithdrawRulePreview,
    receiveReward,
    receiveAllPoints,
    setIsPlaying,
    setLang,
    refresh,
    back,
    openInboxForRewardType,
    startAutoRefresh,
    stopAutoRefresh,
  }
}
